import React, { useState, useEffect } from "react"
import { VStack, HStack, Text, Box, Separator, Skeleton, SkeletonCircle } from "@chakra-ui/react"
import { FaArrowLeft, FaArrowRight, FaLinkSlash } from "react-icons/fa6"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"

import { useLinkableActivities, useLinkableActivitiesByDates } from "@hydrologiq/shared/hooks/activities"
import type { LinkableActivitiesCache } from "@hydrologiq/shared/contexts/ActivityWorkflowContextV2"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "@hydrologiq/shared/utils/toast"
import { formatActivityDate } from "@hydrologiq/shared/utils/activities/dateFormatting"
import { formatActivityKind } from "@hydrologiq/shared/utils/activities/activityStatus"
import { mapStatusToBadge, StatusBadge } from "@hydrologiq/shared/components/StatusBadge"
import { FilterButton } from "@hydrologiq/shared/content/components/Button"

interface ActivityLinkSelectorEditProps {
  mode: "edit"
  activity: gqlTypes.Activity
  onLinkCreated?: (
    targetActivityId: string,
    linkType: "source" | "destination",
    activity: gqlTypes.LinkableActivity,
    removed?: boolean
  ) => void
}

export interface Link {
  targetActivityId: string
  linkType: "source" | "destination"
  siteId?: string // Site ID to populate in the attribute field
  siteName?: string // Site name for display
  activity?: gqlTypes.LinkableActivity // Optional - only needed in edit mode
}

interface ActivityLinkSelectorCreateProps {
  mode: "create"
  assetId?: string
  dateRange?: { start: string; end: string }
  links?: Link[]
  onLinkCreated?: (
    targetActivityId: string,
    linkType: "source" | "destination",
    activity: gqlTypes.LinkableActivity,
    removed?: boolean,
    siteId?: string
  ) => void
  // Caching support
  cachedLinkableActivities?: LinkableActivitiesCache
  onLinkableActivitiesFetched?: (
    before?: gqlTypes.LinkableActivity,
    after?: gqlTypes.LinkableActivity,
    cacheKey?: string
  ) => void
}

type ActivityLinkSelectorProps = ActivityLinkSelectorEditProps | ActivityLinkSelectorCreateProps

export const ACTIVITY_LINK_ENABLED = true
/**
 * Component for linking activities to inherit site information
 * Shows chronologically closest activities before/after current activity
 * Allows linking/unlinking to automatically populate site fields
 *
 * Supports two modes:
 * - edit: Shows linkable activities for an existing activity
 * - create: Shows linkable activities based on date range and asset (for activity creation)
 */
const ActivityLinkSelector: React.FC<ActivityLinkSelectorProps> = (props) => {
  const { mode, onLinkCreated } = props
  const [isLinking, setIsLinking] = useState(false)

  // Edit mode: use existing activity ID
  const editHook = useLinkableActivities(mode === "edit" ? props.activity.id : undefined)

  // Create mode: use date range and asset
  const createHook = useLinkableActivitiesByDates()

  // Get cache props for create mode
  const cachedLinkableActivities = mode === "create" ? props.cachedLinkableActivities : undefined
  const onLinkableActivitiesFetched = mode === "create" ? props.onLinkableActivitiesFetched : undefined

  // Generate cache key based on asset and date range
  const currentCacheKey =
    mode === "create" && props.assetId && props.dateRange?.start && props.dateRange?.end
      ? `${props.assetId}-${props.dateRange.start}-${props.dateRange.end}`
      : undefined

  // Check if cached data is valid for current params
  const isCacheValid = cachedLinkableActivities?.cacheKey === currentCacheKey && currentCacheKey !== undefined

  // Select the appropriate hook based on mode
  const hookData = mode === "edit" ? editHook : createHook

  // Use cached data if valid, otherwise use hook data
  const linkableBefore = isCacheValid ? cachedLinkableActivities?.before : hookData.linkableBefore
  const linkableAfter = isCacheValid ? cachedLinkableActivities?.after : hookData.linkableAfter
  const { loading, createLoading, deleteLoading, error, createLink, deleteLink } = hookData

  const refetchLinkable = mode === "edit" ? editHook.refetchLinkable : undefined

  // Fetch linkable activities for create mode when dates/asset change (skip if cache is valid)
  useEffect(() => {
    if (mode === "create" && props.assetId && props.dateRange?.start && props.dateRange?.end && !isCacheValid) {
      createHook.fetchLinkable(props.assetId, props.dateRange.start, props.dateRange.end)
    }
  }, [
    mode,
    mode === "create" ? props.assetId : undefined,
    mode === "create" ? props.dateRange?.start : undefined,
    mode === "create" ? props.dateRange?.end : undefined,
    isCacheValid,
  ])

  // Notify parent when new data is fetched (for caching)
  useEffect(() => {
    if (
      mode === "create" &&
      onLinkableActivitiesFetched &&
      currentCacheKey &&
      !isCacheValid &&
      !loading &&
      (hookData.linkableBefore !== undefined || hookData.linkableAfter !== undefined)
    ) {
      onLinkableActivitiesFetched(hookData.linkableBefore, hookData.linkableAfter, currentCacheKey)
    }
  }, [hookData.linkableBefore, hookData.linkableAfter, loading, currentCacheKey, isCacheValid])

  const handleLink = async (
    targetActivityId: string,
    linkType: "source" | "destination",
    activity: gqlTypes.LinkableActivity
  ) => {
    // Only allow linking in edit mode
    if (mode !== "edit") return

    let toastId: string | undefined
    try {
      setIsLinking(true)
      toastId = showLoadingToast("Linking activities...")

      await createLink({
        variables: {
          activity: props.activity.id,
          target_activity_id: targetActivityId,
          link_type: linkType,
        },
      })

      dismissToast(toastId)
      showSuccessToast("Activities linked successfully")
      refetchLinkable?.()
      onLinkCreated?.(targetActivityId, linkType, activity, false)
    } catch (err: any) {
      console.error("Error linking activities:", err)
      if (toastId) dismissToast(toastId)
      showErrorToast("Failed to link activities", err.message)
    } finally {
      setIsLinking(false)
    }
  }

  const handleUnlink = async (
    targetActivityId: string,
    linkType: "source" | "destination",
    activity: gqlTypes.LinkableActivity
  ) => {
    // Only allow unlinking in edit mode
    if (mode !== "edit") return

    let toastId: string | undefined
    try {
      setIsLinking(true)
      toastId = showLoadingToast("Unlinking activities...")

      await deleteLink({
        variables: {
          activity: props.activity.id,
          target_activity_id: targetActivityId,
          link_type: linkType,
        },
      })

      dismissToast(toastId)
      showSuccessToast("Activities unlinked successfully")
      refetchLinkable?.()
      onLinkCreated?.(targetActivityId, linkType, activity, true)
    } catch (err: any) {
      console.error("Error unlinking activities:", err)
      if (toastId) dismissToast(toastId)
      showErrorToast("Failed to unlink activities", err.message)
    } finally {
      setIsLinking(false)
    }
  }

  const renderExistingLink = (
    link: gqlTypes.ActivityLink,
    linkType: "source" | "destination",
    activity: gqlTypes.LinkableActivity
  ) => {
    const isDisabled = isLinking || loading || createLoading || deleteLoading

    return (
      <Box
        key={link.activity_id}
        p={3}
        borderWidth="1px"
        borderRadius="md"
        borderColor="green.500"
        bg="green.50"
        _dark={{ bg: "green.900", borderColor: "green.600" }}
      >
        <VStack align="stretch" gap={2}>
          <HStack justify="space-between">
            <HStack gap={2}>
              <Text fontSize="sm" fontWeight="semibold">
                {link.activity_name}
              </Text>
            </HStack>
            <StatusBadge status={mapStatusToBadge(link.activity_status || "")} />
          </HStack>

          <FilterButton
            context={{
              label: "Unlink",
              description: "Unlink this activity from a source or target activity to stop inheriting information.",
              variant: "warning",
            }}
            onClick={() => handleUnlink(link.activity_id, linkType, activity)}
            disabled={isDisabled}
          />
        </VStack>
      </Box>
    )
  }

  const renderLinkableActivity = (linkableActivity: gqlTypes.LinkableActivity, direction: "before" | "after") => {
    if (!linkableActivity) return null

    const site = linkableActivity.site || linkableActivity.destination_site
    const icon = direction === "before" ? <FaArrowLeft /> : <FaArrowRight />
    // Activity before you (earlier in time) = your source (you came FROM there)
    // Activity after you (later in time) = your destination (you go TO there)
    const linkType = direction === "before" ? "destination" : "source"
    const isCreateLinked = (props as ActivityLinkSelectorCreateProps).links?.some(
      (link) => link.targetActivityId === linkableActivity.id && link.linkType === linkType
    )

    return (
      <Box p={3} borderWidth="1px" borderRadius="md" borderColor="border" bg="bg.subtle">
        <VStack align="stretch" gap={2}>
          <HStack justify="space-between">
            <HStack gap={2}>
              {icon}
              <Text fontSize="sm" fontWeight="semibold">
                {formatActivityKind(linkableActivity.kind)}: {linkableActivity.name || linkableActivity.id}
              </Text>
            </HStack>
            <StatusBadge status={mapStatusToBadge(linkableActivity.status || "")} />
          </HStack>

          <Text fontSize="xs" color="fg.muted">
            {formatActivityDate(linkableActivity.start)} - {formatActivityDate(linkableActivity.end)}
          </Text>

          {site && (
            <Box>
              <Text fontSize="xs" fontWeight="semibold">
                Site: {site.name}
              </Text>
            </Box>
          )}

          <FilterButton
            context={{
              label: isCreateLinked ? "Unlink" : "Link",
              description: isCreateLinked
                ? "Unlink this activity from a source or target activity to stop inheriting information."
                : "Link this activity to a source or target activity to automatically inherit information.",
              variant: isCreateLinked ? "warning" : "primary",
            }}
            onClick={() =>
              mode === "edit"
                ? handleLink(linkableActivity.id, linkType, linkableActivity)
                : onLinkCreated?.(linkableActivity.id, linkType, linkableActivity, isCreateLinked, site?.id)
            }
          />
        </VStack>
      </Box>
    )
  }

  const renderLoadingSkeleton = () => (
    <VStack align="stretch" gap={3}>
      <Box>
        <Skeleton height="16px" width="120px" />
      </Box>

      {/* Source Link Section */}
      <VStack align="stretch" gap={2}>
        <Skeleton height="14px" width="80px" />
        <Box p={3} borderWidth="1px" borderRadius="md" borderColor="border" bg="bg.subtle">
          <VStack align="stretch" gap={2}>
            <HStack justify="space-between">
              <HStack gap={2}>
                <SkeletonCircle size="4" />
                <Skeleton height="16px" width="200px" />
              </HStack>
              <Skeleton height="20px" width="60px" />
            </HStack>

            <Skeleton height="12px" width="150px" />

            <Box>
              <Skeleton height="12px" width="100px" />
            </Box>

            <Skeleton height="32px" width="60px" />
          </VStack>
        </Box>
      </VStack>

      {/* Target Link Section */}
      <VStack align="stretch" gap={2}>
        <Skeleton height="14px" width="80px" />
        <Box p={3} borderWidth="1px" borderRadius="md" borderColor="border" bg="bg.subtle">
          <VStack align="stretch" gap={2}>
            <HStack justify="space-between">
              <HStack gap={2}>
                <SkeletonCircle size="4" />
                <Skeleton height="16px" width="180px" />
              </HStack>
              <Skeleton height="20px" width="60px" />
            </HStack>

            <Skeleton height="12px" width="140px" />

            <Box>
              <Skeleton height="12px" width="90px" />
            </Box>

            <Skeleton height="32px" width="50px" />
          </VStack>
        </Box>
      </VStack>

      <Separator />
    </VStack>
  )

  // Show loading for:
  // 1. Hook is loading (edit mode or create mode with active query)
  // 2. Create mode waiting for initial query (has required props but no cached data and no results yet)
  const shouldShowLoading =
    loading ||
    (mode === "create" &&
      props.assetId &&
      props.dateRange?.start &&
      props.dateRange?.end &&
      !isCacheValid &&
      linkableBefore === undefined &&
      linkableAfter === undefined)

  if (shouldShowLoading) {
    return renderLoadingSkeleton()
  }

  if (error) {
    return (
      <Box p={3} borderWidth="1px" borderRadius="md" borderColor="red.200" bg="red.50">
        <Text fontSize="sm" color="red.600">
          Error loading linkable activities
        </Text>
      </Box>
    )
  }

  // Get existing links based on linkType
  const sourceLinks = (props as ActivityLinkSelectorEditProps | undefined)?.activity?.source_links || []
  const targetLinks = (props as ActivityLinkSelectorEditProps | undefined)?.activity?.target_links || []
  const existingLinks = [...sourceLinks, ...targetLinks]

  const hasLinkedBefore = sourceLinks.some((link) => link.activity_id)
  const hasLinkedAfter = targetLinks.some((link) => link.activity_id)
  const hasExistingLinks = existingLinks.length > 0
  const hasLinkableActivities = (linkableBefore && !hasLinkedBefore) || (linkableAfter && !hasLinkedAfter)

  const renderEmptyState = () => (
    <VStack align="stretch" gap={4} p={6} textAlign="center">
      <Box>
        <FaLinkSlash size={32} style={{ color: "var(--chakra-colors-fg-muted)", margin: "0 auto" }} />
      </Box>

      <VStack gap={2} align="stretch">
        <Text fontSize="md" fontWeight="semibold">
          No connectable activities found.
        </Text>

        <Text fontSize="sm" mx="auto">
          You can continue to create this activity without connecting it to any other activities. You can make
          connections after creation.
        </Text>
      </VStack>
    </VStack>
  )

  if (!hasExistingLinks && !hasLinkableActivities) {
    return renderEmptyState()
  }

  console.log(linkableBefore, hasLinkedBefore, linkableAfter, sourceLinks, targetLinks)

  return (
    <VStack align="stretch" gap={3}>
      {/* Show existing links first */}
      <VStack align="stretch" gap={2}>
        {(linkableBefore || sourceLinks.length > 0) && (
          <VStack align="stretch" gap={2}>
            <Text fontSize="sm" fontWeight="semibold">
              Source Connection
            </Text>
            {linkableBefore && !hasLinkedBefore
              ? renderLinkableActivity(linkableBefore, "before")
              : linkableBefore && sourceLinks.map((link) => renderExistingLink(link, "destination", linkableBefore))}
          </VStack>
        )}
        {(linkableAfter || targetLinks.length > 0) && (
          <VStack align="stretch" gap={2}>
            <Text fontSize="sm" fontWeight="semibold">
              Target Connection
            </Text>
            {linkableAfter && !hasLinkedAfter
              ? renderLinkableActivity(linkableAfter, "after")
              : linkableAfter && targetLinks.map((link) => renderExistingLink(link, "source", linkableAfter))}
          </VStack>
        )}
      </VStack>

      <Separator />
    </VStack>
  )
}

export default ActivityLinkSelector
